import {
  AccountDropdownDto,
  ExtOperStatus,
  ProjectDropdownDto,
  ResourceDto,
  ResourceEditableFieldsDto,
  ResourceStatus,
  Seniority,
} from './accounts';
import { FileRawDto } from './file';
import { IdField, TimestampsFields } from './generic';
import { UserProfileDto } from './user';

// --- --- --- --- --- sow-change-req and sow-change-req-dto types from FSS --- --- --- --- ---

// SowChangeReq types

export enum BusinessAction {
  MOVE_RESOURCE = 'MOVE_RESOURCE',
  EDIT_RESOURCE = 'EDIT_RESOURCE',
  DEACTIVATE_RESOURCE = 'DEACTIVATE_RESOURCE',
}

export enum ChReqStatus {
  PENDING = 'PENDING',
  DONE = 'DONE',
  REJECTED = 'REJECTED',
}

export enum SowChangeReqMode {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export const SowChangeReqModes = Object.values(SowChangeReqMode);

export const chReqStatusLabel = {
  [ChReqStatus.PENDING]: 'Pending',
  [ChReqStatus.DONE]: 'Approved',
  [ChReqStatus.REJECTED]: 'Rejected',
};

export interface CreateSowChangeReqDto {
  id: string;
  reqStatus: ChReqStatus;
  approvedById?: string | null;
  approvedOn?: string | null; // On FE Date converts to string for API
  raisedById?: string | null;
  userNotes?: string | null;
  adminNotes?: string | null;
  declineNotes?: string | null;
  action: BusinessAction;
  resourceChReqs?: CreateResourceChReqForUpdateDto[];
}

export interface SowChangeReqDto
  extends CreateSowChangeReqDto,
    TimestampsFields,
    IdField {}

export interface CreateResourceChReqForCreateDto {
  mode: 'resource-create';
  projectId: string;
  userId: string;
  seniority: Seniority;
  position: string;
  billRate: number;
  allocation: number;
  email: string | null;
  sowLink: string | null;
  startedAt: string | null;
  endedAt: string | null;
  status: ResourceStatus;
}

export interface CreateResourceChReqForUpdateDto
  extends Pick<
    Partial<CreateResourceChReqForCreateDto>,
    | 'seniority'
    | 'position'
    | 'billRate'
    | 'allocation'
    | 'startedAt'
    | 'endedAt'
    | 'status'
    | 'email'
    | 'sowLink'
  > {
  // userId and projectId are uneditable, but they are always present for easily determining what this action belongs to
  projectId: string;
  userId: string;
  mode: 'resource-update';
  resourceId: string;
}

export interface CreateResourceChReqForDeactivateDto
  extends Pick<
    Partial<CreateResourceChReqForCreateDto>,
    | 'seniority'
    | 'position'
    | 'billRate'
    | 'allocation'
    | 'startedAt'
    | 'endedAt'
    | 'status'
    | 'email'
    | 'sowLink'
  > {
  // userId and projectId are uneditable, but they are always present for easily determining what this action belongs to
  projectId: string;
  userId: string;
  mode: 'resource-deactivate';
  resourceId: string;
}

export interface CreateResourceChReqDto
  extends Pick<
    Partial<CreateResourceChReqForCreateDto>,
    | 'projectId'
    | 'userId'
    | 'seniority'
    | 'position'
    | 'billRate'
    | 'allocation'
    | 'startedAt'
    | 'endedAt'
    | 'status'
  > {
  mode?: SowChangeReqMode | null;
  sowChangeReqId: string;
  resourceId: string;
  order: number;
}

export interface SowChangeReqFullDto extends SowChangeReqDto {
  raisedBy: UserProfileDto;
  approvedBy: UserProfileDto | null;
  change:
    | EditResourceChReqDto
    | MoveResourceChReqDto
    | DeactivateResourceChReqDto;
  resourceChReqs?: any[];
  files: FileRawDto[];
}

export interface MoveResourceChReqDto {
  action: BusinessAction.MOVE_RESOURCE;
  newResource: CreateResourceChReqForCreateDto;
  oldResource: CreateResourceChReqForUpdateDto;
  prevResource: ResourceEditableFieldsDto;
  user: UserProfileDto;
  oldProject: ProjectDropdownDto;
  newProject: ProjectDropdownDto;
  account: AccountDropdownDto;
  sowChangeStatus: ExtOperStatus | null;
  zpChangeStatus: ExtOperStatus | null;
}

export interface EditResourceChReqDto {
  action: BusinessAction.EDIT_RESOURCE;
  resourceChReq: CreateResourceChReqForUpdateDto;
  prevResource: ResourceEditableFieldsDto;
  user: UserProfileDto;
  sowChangeStatus: ExtOperStatus | null;
  project: ProjectDropdownDto;
  account: AccountDropdownDto;
}

export interface DeactivateResourceChReqDto {
  action: BusinessAction.DEACTIVATE_RESOURCE;
  resourceChReq: CreateResourceChReqForDeactivateDto;
  prevResource: ResourceEditableFieldsDto;
  user: UserProfileDto;
  sowChangeStatus: ExtOperStatus | null;
  project: ProjectDropdownDto;
  account: AccountDropdownDto;
}

export type SowChangeReqForApprovalDto = Pick<
  SowChangeReqFullDto,
  | 'id'
  | 'raisedBy'
  | 'reqStatus'
  | 'userNotes'
  | 'adminNotes'
  | 'action'
  | 'createdAt'
  | 'resourceChReqs'
>;

export interface SowChangeReqForApprovalRespDto {
  sowChangeReqs: SowChangeReqForApprovalDto[];
  sowSpreadsheetLink: string;
}

export interface ExecuteSowChangeReqRespDto {
  idType: 'ExecuteSowChangeReqRespDto';
  createdResources: ResourceDto[];
  updatedResources: ResourceDto[];
  emailSentStatus?: ExtOperStatus;
  sowAppendStatus?: ExtOperStatus;
  approvedByName?: string;
  resourceChReqs?: CreateResourceChReqForUpdateDto[];
}
